import * as React from 'react';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import QuestionStepper from 'Components/QuestionStepper';
import FormSection from 'Components/FormSection';
import { styled } from '@mui/system';
import { steps as chopINTENDSteps, getStepContent as getCHOPINTENDSteps } from '../chopINTENDSettings';
import { getPatientAgeInMonthsWithOneDecimal } from 'Utility/patientInfo';
import { showPatientAgeInMonthsOnForm } from '../../../utils';
import InfoPopover from 'Components/InfoPopover';
import colors from '../../../../../config/theme/colors';
import { useIntl } from 'react-intl';
import { Container, Item } from 'Components/Grid';
import SimpleList from 'Components/SimpleList';
import { getMissingFields } from 'Utility/documentHandling';
import { chop_intend } from 'neuro-schemas';

const { calculateScore } = chop_intend;

const Text = styled('div')({
  color: colors.tertiaryText,
});

const testingEnvironmentFields = ['ideally', 'paddedMat', 'diaper', 'redBall', 'pacifier', 'cnt'];

const CHOPINTENDForm = ({ formData, viewing }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const mainSteps = chopINTENDSteps.flatMap(s => Object.keys(s));
  return (
    <React.Fragment>
      <FormRow title="motor.labels.chopINTEND.testingEnvironment.title">
        <Container>
          <Item>
            <Text>
              <React.Fragment>
                {testingEnvironmentFields.map(
                  (field: string) => (
                    <React.Fragment key={field}>
                      {fm(`motor.labels.chopINTEND.testingEnvironment.${field}`)}
                      <br />
                    </React.Fragment>
                  ),
                  testingEnvironmentFields,
                )}
              </React.Fragment>
            </Text>
          </Item>
        </Container>
      </FormRow>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!viewing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow title="general.timeOfDay" id={'time'}>
        <InputHandler type="TimePicker" editing={!viewing} name="time" formData={formData} timeDefault="now" />
      </FormRow>
      <FormRow title="motor.hoursSinceLastFeeding">
        <InputHandler
          type="NumberField"
          editing={!viewing}
          name="hoursSinceLastFeeding"
          formData={formData}
          placeholder={'motor.hoursSinceLastFeedingShort'}
          min={0}
        />
      </FormRow>
      <FormRow title="motor.ageMonth" condition={showPatientAgeInMonthsOnForm(formData.document.date)}>
        <InputHandler
          type="TextField"
          editing={false}
          name="age"
          formData={{
            onChange: formData.onChange,
            document: {
              age: formData.document.date ? getPatientAgeInMonthsWithOneDecimal(formData.document.date) : '',
            },
          }}
          disabled={true}
        />
      </FormRow>
      <FormSection>
        <QuestionStepper
          viewing={viewing}
          formData={formData}
          pageType={'motor'}
          docType={'chopINTEND'}
          steps={chopINTENDSteps}
          getStepContent={getCHOPINTENDSteps}
        />
      </FormSection>
      <FormSection>
        <FormRow
          title={
            calculateScore(formData.document) === formData.document.manualScore && formData.document.manualScore != null
              ? 'motor.manuallyGivenScore'
              : 'motor.automaticScore'
          }
        >
          <Container style={{ fontSize: '1.8rem', fontWeight: 600, color: colors.primary }}>
            {calculateScore(formData.document) === null ? (
              <InfoPopover
                text={
                  <SimpleList
                    title={fm('general.couldntCalculate')}
                    listItems={getMissingFields(
                      chopINTENDSteps.map(s => Object.values(s)).flat(2),
                      formData.document,
                    ).map(
                      f =>
                        `${mainSteps.findIndex(step => f.includes(step)) + 1}. ${fm(`motor.labels.chopINTEND.${f}`)}`,
                    )}
                  />
                }
                color="primary"
              />
            ) : (
              calculateScore(formData.document)
            )}
          </Container>
        </FormRow>
        <FormRow title="motor.manualScore">
          <Container style={{ fontSize: '1.8rem', fontWeight: 600, color: colors.primary }}>
            <InputHandler
              type="NumberField"
              editing={!viewing}
              name="manualScore"
              formData={formData}
              width={4}
              height={3}
              maxLength={2}
            />
          </Container>
        </FormRow>
      </FormSection>
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'chop_intend'>>;
  viewing: boolean;
}

export default CHOPINTENDForm;
